const effectiveWidth = document.documentElement.clientWidth || window.innerWidth;
window.mobileGalleryLocale = window.mobileGalleryLocale || {
  noImageText: "{{ 'products.product.media.no_image_available' | t | default: 'Sorry, this image is not available.' }}"
};
if (!customElements.get("mobile-media-gallery")) {
  class MobileMediaGallery extends HTMLElement {
    constructor() {
      super();

      // Debounced resize handler
      this.resizeHandler = this.debounce(() => this.checkAndInit(), 100);
      window.addEventListener('resize', this.resizeHandler);

      // Initialize faster on mobile
      setTimeout(() => this.checkAndInit(), 100);
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.resizeHandler);
      this.destroy();
    }

    debounce(func, wait) {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    }

    checkAndInit() {
       // Recalculate width on every check
      const currentWidth = document.documentElement.clientWidth || window.innerWidth;
      const isMobile = currentWidth < 769;

      if (isMobile && !this.initialized) {
        this.init();
      } else if (!isMobile && this.initialized) {
        this.destroy();
      }
    }

    init() {
      if (this.initialized) return;

      this.parentGallery = this.closest('media-gallery');
      if (!this.parentGallery) return;

      this.section = this.parentGallery.closest('.js-product');
      this.initialized = true;
      this.buildSlider();
      this.hideDesktopGallery();
      this.setupEventListeners();
    }

    destroy() {
      if (!this.initialized) return;

      if (this.paginationObserver) {
        this.paginationObserver.disconnect();
        this.paginationObserver = null;
      }

      this.showDesktopGallery();
      this.removeMobileSlider();
      this.hideNoImageOverlay();
      this.removeEventListeners();
      this.initialized = false;
    }

    setupEventListeners() {
      if (this.section) {
        this.variantChangeHandler = this.handleVariantChange.bind(this);
        this.section.addEventListener('on:variant:change', this.variantChangeHandler);
      }
    }

    removeEventListeners() {
      if (this.section && this.variantChangeHandler) {
        this.section.removeEventListener('on:variant:change', this.variantChangeHandler);
      }
    }

    hideDesktopGallery() {
      ['.media-gallery__viewer', '.media-gallery__thumbs', '.media-ctrl'].forEach(selector => {
        const element = this.parentGallery.querySelector(selector);
        if (element) element.style.display = 'none';
      });
    }

    showDesktopGallery() {
      ['.media-gallery__viewer', '.media-gallery__thumbs', '.media-ctrl'].forEach(selector => {
        const element = this.parentGallery.querySelector(selector);
        if (element) element.style.display = '';
      });
    }

    buildSlider() {
      const variantOption = this.getCurrentVariantOption();
      let variantMedia = variantOption ? this.getVariantMediaItems(variantOption) : [];

      if (variantMedia.length === 0) {
        variantMedia = this.getAllMediaItems();

        if (variantMedia.length === 0) {
          this.showNoImageOverlay();
          return;
        }
      }

      this.hideNoImageOverlay();
      const lifestyleData = this.getLifestyleData();
      this.createSlider(variantMedia, lifestyleData);
    }

    getCurrentVariantOption() {

      // Try desktop gallery first
      if (this.parentGallery?.mediaGroupingEnabled && this.parentGallery.getMediaGroupFromOptionSelectors) {
        const variantOption = this.parentGallery.getMediaGroupFromOptionSelectors();
        return variantOption;
      }

      // Fallback to option selectors
      const optionSelectors = this.section?.querySelectorAll('.option-selector');
      if (optionSelectors?.length > 0) {
        const selectedOptions = [];
        optionSelectors.forEach(selector => {
          if (selector.dataset.selectorType === 'dropdown') {
            const selectedOption = selector.querySelector('.custom-select__btn');
            selectedOptions.push(selectedOption ? selectedOption.textContent.trim() : null);
          } else {
            const checkedInput = selector.querySelector('input:checked');
            selectedOptions.push(checkedInput ? checkedInput.value : null);
          }
        });
        const variantOption = selectedOptions.filter(Boolean).join(' / ');
        return variantOption;
      }

      return null;
    }

    getVariantMediaItems(variantOption) {
      if (this.parentGallery?.getMediaGroupMap) {
        try {
          const mediaGroupMap = this.parentGallery.getMediaGroupMap();
          let targetGroup = mediaGroupMap.groups[variantOption];
          if (targetGroup?.items) {
            const items = targetGroup.items
              .map(item => item.main)
              .filter(item => !item.hasAttribute('data-lifestyle-content'));

            if (items.length > 0) {
              return items;
            }
          }

          if (variantOption && variantOption.includes(' / ')) {
            const options = variantOption.split(' / ');

            for (const singleOption of options) {
              const trimmedOption = singleOption.trim();
              const singleGroup = mediaGroupMap.groups[trimmedOption];
              if (singleGroup?.items) {
                const items = singleGroup.items
                  .map(item => item.main)
                  .filter(item => !item.hasAttribute('data-lifestyle-content'));

                if (items.length > 0) {
                  return items;
                }
              }
            }

            const availableGroups = Object.keys(mediaGroupMap.groups);
            for (const singleOption of options) {
              const trimmedOption = singleOption.trim();
              const startMatches = availableGroups.filter(groupKey =>
                groupKey.startsWith(trimmedOption) && groupKey !== variantOption
              );

              for (const match of startMatches) {
                const group = mediaGroupMap.groups[match];
                if (group?.items) {
                  const items = group.items
                    .map(item => item.main)
                    .filter(item => !item.hasAttribute('data-lifestyle-content'));

                  if (items.length > 0) {
                    return items;
                  }
                }
              }

              const containsMatches = availableGroups.filter(groupKey =>
                groupKey.includes(trimmedOption) && groupKey !== variantOption && !groupKey.startsWith(trimmedOption)
              );

              for (const match of containsMatches) {
                const group = mediaGroupMap.groups[match];
                if (group?.items) {
                  const items = group.items
                    .map(item => item.main)
                    .filter(item => !item.hasAttribute('data-lifestyle-content'));

                  if (items.length > 0) {
                    return items;
                  }
                }
              }
            }
          }
        } catch (e) {
          console.warn('[mobile-media-gallery] Error in getVariantMediaItems:', e);
          return [];
        }
      }
      return [];
    }

    getAllMediaItems() {
      const allMediaItems = this.parentGallery?.querySelectorAll('.media-viewer__item[data-media-id]');
      return Array.from(allMediaItems || []).filter(item => {
        return item.dataset.mediaId && !item.hasAttribute('data-lifestyle-content');
      });
    }

    getLifestyleData() {
      const lifestyleItems = this.parentGallery?.querySelectorAll('[data-lifestyle-content="true"]');
      let lifestyleImage = null;
      let lifestyleVideo = null;
      let lifestyleShopifyVideo = null;

      lifestyleItems?.forEach((item) => {
        const iframe = item.querySelector('iframe[src*="vimeo"]');
        if (iframe) {
          const match = iframe.src.match(/video\/(\d+)/);
          if (match) lifestyleVideo = match[1];
        }
        // Shopify hosted video
        const shopifyVideoSource = item.querySelector("video source");
        const shopifyVideo = item.querySelector("video");

        if (shopifyVideoSource?.src) {
          lifestyleShopifyVideo = shopifyVideoSource.src;
        } else if (shopifyVideo?.src) {
          lifestyleShopifyVideo = shopifyVideo.src;
        }
        // Image
        const img = item.querySelector('img');
        if (img) lifestyleImage = img.src.split('?')[0];
      });

      return {lifestyleImage, lifestyleVideo, lifestyleShopifyVideo};
    }

    createSlider(variantMediaItems, lifestyleData) {
      this.removeMobileSlider();

      this.mobileContainer = document.createElement('div');
      this.mobileContainer.className = 'mobile-media-gallery';

      // Create slides with simple lazy loading
      this.mobileContainer.innerHTML = this.generateSliderHTML(variantMediaItems, lifestyleData);

      this.appendChild(this.mobileContainer);

      // Initialize Swiper faster
      setTimeout(() => this.initializeSwiper(), 50); // Reduced from 300ms
    }

    removeMobileSlider() {
      if (this.mobileContainer) {
        this.mobileContainer.remove();
        this.mobileContainer = null;
      }
      this.innerHTML = '';
    }

    generateSliderHTML(variantMediaItems, lifestyleData) {
      let slidesHTML = '';
      let position = 0;

      // CRITICAL FIX: Check if lifestyle content is already in variantMediaItems
      const hasLifestyleInVariantMedia = variantMediaItems.some(item =>
        item.hasAttribute('data-lifestyle-content')
      );

      // Add first variant image
      if (variantMediaItems.length > 0) {
        slidesHTML += this.createMediaSlide(variantMediaItems[0], ++position);
      }

      // Only add lifestyle content separately if it's NOT already in variant media
      if (!hasLifestyleInVariantMedia) {
        if (lifestyleData.lifestyleShopifyVideo) {
          slidesHTML += this.createShopifyVideoSlide(lifestyleData.lifestyleShopifyVideo, ++position);
        } else if (lifestyleData.lifestyleVideo) {
            slidesHTML += this.createLifestyleVideoSlide(lifestyleData.lifestyleVideo, ++position);
        } else if (lifestyleData.lifestyleImage) {
            slidesHTML += this.createLifestyleImageSlide(lifestyleData.lifestyleImage, ++position);
        }
      }

      // Add remaining variant media
      variantMediaItems.slice(1).forEach((item) => {
        slidesHTML += this.createMediaSlide(item, ++position);
      });

      if (!slidesHTML) return '<div class="error">No media found</div>';

      return `
        <swiper-container
          class="mobile-swiper"
          slides-per-view="1"
          space-between="0"
          loop="false"
          pagination="true"
          pagination-clickable="true"
          navigation="false"
          keyboard="true"
          a11y="false"
        >
          ${slidesHTML}
        </swiper-container>
      `;
    }

    createMediaSlide(mediaItem, position) {
      return `
        <swiper-slide data-media-id="${mediaItem.dataset.mediaId}" data-position="${position}">
          <div class="slide-content">${mediaItem.innerHTML}</div>
        </swiper-slide>
      `;
    }

    createLifestyleImageSlide(imageSrc, position) {
      const width = effectiveWidth;
      const height = Math.round(width * 1.218);

      let cleanSrc = imageSrc;
      if (cleanSrc.startsWith('//')) cleanSrc = 'https:' + cleanSrc;
      cleanSrc = cleanSrc.split('?')[0];

      const mainImageUrl = `${cleanSrc}?width=${width}`;
      const srcset = `${cleanSrc}?width=${width}, ${cleanSrc}?width=${width * 2} 2x`;

      return `
        <swiper-slide data-lifestyle="image" data-position="${position}">
          <div class="slide-content">
            <img src="${mainImageUrl}" srcset="${srcset}" alt="Lifestyle" width="${width}" height="${height}" loading="lazy" />
          </div>
        </swiper-slide>
      `;
    }

    createLifestyleVideoSlide(vimeoId, position) {
      const height = Math.round(effectiveWidth * 1.218);

      return `
        <swiper-slide data-lifestyle="video" data-position="${position}">
          <div class="slide-content">
            <iframe
              src="https://player.vimeo.com/video/${vimeoId}?title=0&muted=1&autoplay=1&autopause=0&loop=1&background=1"
              width="100%"
              height="${height}"
              frameborder="0"
              allow="autoplay; fullscreen; picture-in-picture"
            ></iframe>
          </div>
        </swiper-slide>
      `;
    }

    createShopifyVideoSlide(videoSrc, position) {
      return `
        <swiper-slide
          data-lifestyle="shopify-video"
          data-position="${position}"
        >
          <div
            class="media relative"
            style="padding-top: 122.50398137939482%;"
          >
            <div
              class="absolute top-0 left-0 w-full h-full overflow-hidden"
            >
              <video
                playsinline
                webkit-playsinline
                autoplay
                muted
                loop
                controls
                preload="metadata"
                style="width: 100%;height: 100%;object-fit: cover;display: block;"
              >
                <source
                  src="${videoSrc}"
                  type="video/mp4"
                >
              </video>
            </div>
          </div>
        </swiper-slide>
      `;
    }

    initializeSwiper() {
      this.swiper = this.mobileContainer?.querySelector('swiper-container');
      if (!this.swiper) return;

      // More efficient swiper checking
      let attempts = 0;
      const checkSwiper = setInterval(() => {
        attempts++;
        if (this.swiper.swiper || attempts >= 10) { // Reduced max attempts
          clearInterval(checkSwiper);
          if (this.swiper.swiper) {
            this.swiper.swiper.update();
            const videos = this.mobileContainer.querySelectorAll("video");

              videos.forEach((video) => {
                video.muted = true;
                video.play().catch(() => {});
              });

            document.querySelectorAll("swiper-container").forEach(function (el) {
              el.classList.add("swiper-container--initialized");
            });

            setTimeout(() => this.fixPaginationDots(), 50); // Reduced delay

            const slideCount = this.swiper.querySelectorAll('swiper-slide').length;
            if (slideCount > 13) {
              this.swiper.swiper.on('slideChange', () => {
                setTimeout(() => this.scrollActiveDotIntoView(), 50);
              });
            }
          }
        }
      }, 50); // Check more frequently
    }

    fixPaginationDots() {
      try {
        const shadowRoot = this.swiper?.shadowRoot;
        if (!shadowRoot) return;

        const pagination = shadowRoot.querySelector('.swiper-pagination');
        if (!pagination) return;

        const bullets = shadowRoot.querySelectorAll('.swiper-pagination-bullet');
        const slideCount = bullets.length;

        if (slideCount > 13) {
          pagination.style.cssText = `
            width: auto !important;
            max-width: 90% !important;
            padding: 8px 16px !important;
            border-radius: 20px !important;
            overflow-x: auto !important;
            overflow-y: hidden !important;
            white-space: nowrap !important;
            scrollbar-width: none !important;
            -ms-overflow-style: none !important;
            justify-content: flex-start !important;
          `;

          bullets.forEach(bullet => {
            bullet.style.cssText = `
              width: 8px !important;
              height: 8px !important;
              margin: 0 10px !important;
              background: #ffffff !important;
              border: 1px solid #000000 !important;
              padding: 0 !important;
              border-radius: 50% !important;
              opacity: 1 !important;
              transition: all 0.3s ease !important;
              display: inline-block !important;
              flex-shrink: 0 !important;
            `;
          });

          const activeBullet = shadowRoot.querySelector('.swiper-pagination-bullet-active');
          if (activeBullet) {
            activeBullet.style.cssText = `
              width: 8px !important;
              height: 8px !important;
              margin: 0 10px !important;
              background: #000000 !important;
              border: 1px solid #000000 !important;
              padding: 0 !important;
              border-radius: 50% !important;
              opacity: 1 !important;
              transition: all 0.3s ease !important;
              display: inline-block !important;
              flex-shrink: 0 !important;
              transform: scale(1.1) !important;
            `;
          }

          // Only set up observer for scrollable pagination
          this.setupPaginationObserver(shadowRoot);
        }

      } catch (error) {
        console.warn('Could not fix pagination dots:', error);
      }
    }

    setupPaginationObserver(shadowRoot) {
      if (this.paginationObserver) {
        this.paginationObserver.disconnect();
      }

      const pagination = shadowRoot.querySelector('.swiper-pagination');
      if (!pagination) return;

      this.paginationObserver = new MutationObserver(() => {
        setTimeout(() => this.fixPaginationDots(), 50);
      });

      this.paginationObserver.observe(pagination, {
        childList: true,
        attributes: true
      });
    }

    scrollActiveDotIntoView() {
      try {
        const shadowRoot = this.swiper?.shadowRoot;
        if (!shadowRoot) return;

        const pagination = shadowRoot.querySelector('.swiper-pagination');
        const activeBullet = shadowRoot.querySelector('.swiper-pagination-bullet-active');

        if (!pagination || !activeBullet) return;

        const containerWidth = pagination.offsetWidth;
        const activeDotLeft = activeBullet.offsetLeft;
        const activeDotWidth = activeBullet.offsetWidth;
        const scrollLeft = activeDotLeft - (containerWidth / 2) + (activeDotWidth / 2);

        pagination.scrollTo({
          left: scrollLeft,
          behavior: 'smooth'
        });

      } catch (error) {
        console.warn('Could not scroll active dot into view:', error);
      }
    }

    handleVariantChange(evt) {
      if (!this.initialized || effectiveWidth > 768) return;

      const variant = evt?.detail?.variant;
      if (variant) {
        if (variant.featured_media) {
          this.hideNoImageOverlay();
          this.buildSlider();
        } else {
          this.showNoImageOverlay();
        }
      } else {
        this.buildSlider();
      }
    }

    hasCurrentVariantMedia() {
      const variantOption = this.getCurrentVariantOption();
      if (!variantOption) return false;

      const variantMedia = this.getVariantMediaItems(variantOption);
      return variantMedia.length > 0;
    }

    showNoImageOverlay() {
      this.hideNoImageOverlay();
      this.removeMobileSlider();

      const overlay = document.createElement('div');
      overlay.className = 'mobile-media-gallery__no-image-overlay';
      overlay.innerHTML = `
        <div class="mobile-media-gallery__no-image-content">
          <div class="mobile-media-gallery__no-image-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M21 19V5C21 3.9 20.1 3 19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19ZM8.5 13.5L11 16.51L14.5 12L19 18H5L8.5 13.5Z" fill="currentColor"/>
              <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" stroke-width="2"/>
            </svg>
          </div>
          <p class="mobile-media-gallery__no-image-text">${window.mobileGalleryLocale?.noImageText}</p>
        </div>
      `;

      this.mobileContainer = document.createElement('div');
      this.mobileContainer.className = 'mobile-media-gallery';
      this.mobileContainer.appendChild(overlay);
      this.appendChild(this.mobileContainer);
    }

    hideNoImageOverlay() {
      const existingOverlay = this.querySelector('.mobile-media-gallery__no-image-overlay');
      if (existingOverlay) {
        existingOverlay.remove();
      }
    }
  }

  customElements.define("mobile-media-gallery", MobileMediaGallery);
}

function initializeMobileGalleries() {
  document.querySelectorAll('media-gallery').forEach((gallery) => {
    if (!gallery.querySelector('mobile-media-gallery')) {
      gallery.appendChild(document.createElement('mobile-media-gallery'));
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeMobileGalleries);
} else {
  setTimeout(initializeMobileGalleries, 50);
}

document.addEventListener('shopify:section:load', () => {
  setTimeout(initializeMobileGalleries, 50);
});